import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexDataMigration } from "../../../services/convexService";
import { toast } from "sonner";
import { useLanguage } from "../../../contexts/LanguageContext";

const DataMigration = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { updateProjectsWithSalesToSellPurpose, migrateExistingProjects, migrateProjectsWithPurpose, updateAllProjectsWithPurpose } = useConvexDataMigration();

  const [loading, setLoading] = useState({
    updateProjectsWithSalesToSellPurpose: false,
    migrateExistingProjects: false,
    migrateProjectsWithPurpose: false,
    updateAllProjectsWithPurpose: false,
  });

  // Check if user is admin
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">You don't have permission to access this page.</p>
          <Button onClick={() => navigate("/")} className="w-full">Go Home</Button>
        </div>
      </div>
    );
  }

  const handleMigration = async (migrationName: keyof typeof loading, migrationFunction: any, purpose?: "sell" | "getInvestment") => {
    setLoading(prev => ({ ...prev, [migrationName]: true }));

    try {
      let result;
      if (migrationName === 'migrateProjectsWithPurpose' || migrationName === 'updateAllProjectsWithPurpose') {
        if (!purpose) {
          toast.error("Purpose is required for this migration");
          return;
        }
        result = await migrationFunction({ purpose });
      } else {
        result = await migrationFunction({});
      }

      toast.success(result.message || `Migration ${migrationName} completed successfully!`);
    } catch (error: any) {
      console.error(`Error running migration ${migrationName}:`, error);
      toast.error(error.message || `Failed to run migration ${migrationName}`);
    } finally {
      setLoading(prev => ({ ...prev, [migrationName]: false }));
    }
  };

  return (
    <div className="min-h-screen w-full px-4 py-6 sm:px-6 lg:px-8" dir="ltr">
      <div className="max-w-4xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Data Migration Tools</h1>
          <p className="text-muted-foreground">
            Administrative tools for data migration and updates
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Update projects with sales to sell purpose */}
          <Card>
            <CardHeader>
              <CardTitle>Update Projects with Sales to Sell Purpose</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Updates all projects that have existing project sales to have the purpose "sell"
              </p>
              <Button
                onClick={() => handleMigration('updateProjectsWithSalesToSellPurpose', updateProjectsWithSalesToSellPurpose)}
                disabled={loading.updateProjectsWithSalesToSellPurpose}
                className="w-full"
              >
                {loading.updateProjectsWithSalesToSellPurpose ? "Running..." : "Run Migration"}
              </Button>
            </CardContent>
          </Card>

          {/* Migrate existing projects */}
          <Card>
            <CardHeader>
              <CardTitle>Migrate Existing Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Updates existing projects that don't have a purpose set to default to "getInvestment"
              </p>
              <Button
                onClick={() => handleMigration('migrateExistingProjects', migrateExistingProjects)}
                disabled={loading.migrateExistingProjects}
                className="w-full"
              >
                {loading.migrateExistingProjects ? "Running..." : "Run Migration"}
              </Button>
            </CardContent>
          </Card>

          {/* Migrate projects with specific purpose */}
          <Card>
            <CardHeader>
              <CardTitle>Migrate Projects with Specific Purpose</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Updates existing projects without a purpose to a specific purpose
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleMigration('migrateProjectsWithPurpose', migrateProjectsWithPurpose, 'sell')}
                  disabled={loading.migrateProjectsWithPurpose}
                  variant="outline"
                  className="flex-1"
                >
                  {loading.migrateProjectsWithPurpose ? "Running..." : "Set to Sell"}
                </Button>
                <Button
                  onClick={() => handleMigration('migrateProjectsWithPurpose', migrateProjectsWithPurpose, 'getInvestment')}
                  disabled={loading.migrateProjectsWithPurpose}
                  variant="outline"
                  className="flex-1"
                >
                  {loading.migrateProjectsWithPurpose ? "Running..." : "Set to Investment"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Update all projects to specific purpose */}
          <Card>
            <CardHeader>
              <CardTitle>Update All Projects to Specific Purpose</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Updates ALL projects to have a specific purpose (use with caution)
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleMigration('updateAllProjectsWithPurpose', updateAllProjectsWithPurpose, 'sell')}
                  disabled={loading.updateAllProjectsWithPurpose}
                  variant="destructive"
                  className="flex-1"
                >
                  {loading.updateAllProjectsWithPurpose ? "Running..." : "Set All to Sell"}
                </Button>
                <Button
                  onClick={() => handleMigration('updateAllProjectsWithPurpose', updateAllProjectsWithPurpose, 'getInvestment')}
                  disabled={loading.updateAllProjectsWithPurpose}
                  variant="destructive"
                  className="flex-1"
                >
                  {loading.updateAllProjectsWithPurpose ? "Running..." : "Set All to Investment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <Button variant="outline" onClick={() => navigate("/admin")}>
            Back to Admin Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataMigration;